import { fetchArtworkPreview } from './artworkPreviewTransport'
import { withUploadDeadline } from './uploadDeadline'

// The builder shell issues this cookie; the server requires it echoed back in a
// header. That is what distinguishes a real submission from a cross-origin page
// riding the SameSite=None session cookie — a forged page can send the request
// but cannot read our cookies. Deliberately separate from Rails' own
// X-CSRF-Token, which is bound to the SameSite=Lax session cookie and so is
// absent inside the Shopify embed.
export function builderCsrfToken() {
  const entry = document.cookie.split('; ').find(pair => pair.startsWith('builder_csrf='))
  return entry ? decodeURIComponent(entry.slice('builder_csrf='.length)) : ''
}

export function buildHeaders(builderSessionId, builderDraftPublicId) {
  const headers = {
    'Content-Type': 'application/json',
    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
    'X-Builder-CSRF': builderCsrfToken(),
    'X-Builder-Session': builderSessionId
  }
  if (builderDraftPublicId) headers['X-Builder-Draft'] = builderDraftPublicId
  return headers
}

export function normalizeUpload(raw) {
  const {
    status,
    processing_status,
    processing_error,
    width_px,
    height_px,
    content_box,
    has_transparency,
    background_detected,
    background_color,
    background_removal_mode,
    background_processing_status,
    background_processing_error,
    background_revision,
    original_filename,
    filename,
    byte_size,
    image_url,
    preview_url,
    print_width_in,
    print_height_in,
    vendor_artwork_public_id,
    ...rest
  } = raw

  const normalized = {
    ...rest,
    processingStatus: rest.processingStatus ?? status ?? processing_status,
    processingError: rest.processingError ?? processing_error,
    widthPx: rest.widthPx ?? width_px,
    heightPx: rest.heightPx ?? height_px,
    contentBox: rest.contentBox ?? content_box,
    hasTransparency: rest.hasTransparency ?? has_transparency,
    backgroundDetected: rest.backgroundDetected ?? background_detected ?? false,
    backgroundColor: rest.backgroundColor ?? background_color,
    backgroundRemovalMode: rest.backgroundRemovalMode ?? background_removal_mode,
    backgroundProcessingStatus: rest.backgroundProcessingStatus ?? background_processing_status ?? 'idle',
    backgroundProcessingError: rest.backgroundProcessingError ?? background_processing_error,
    backgroundRevision: rest.backgroundRevision ?? background_revision ?? 0,
    originalFilename: rest.originalFilename ?? original_filename ?? filename,
    byteSize: rest.byteSize ?? byte_size,
    imageUrl: rest.imageUrl ?? image_url,
    previewUrl: rest.previewUrl ?? preview_url,
    printWidthIn: rest.printWidthIn ?? print_width_in,
    printHeightIn: rest.printHeightIn ?? print_height_in,
  }

  const vendorArtworkPublicId = rest.vendorArtworkPublicId ?? vendor_artwork_public_id
  if (vendorArtworkPublicId != null) normalized.vendorArtworkPublicId = vendorArtworkPublicId

  return normalized
}

export function normalizeVendorArtwork(raw) {
  const {
    public_id,
    original_filename,
    width_px,
    height_px,
    preview_url,
    ...rest
  } = raw

  return {
    ...rest,
    publicId: rest.publicId ?? public_id,
    originalFilename: rest.originalFilename ?? original_filename,
    widthPx: rest.widthPx ?? width_px,
    heightPx: rest.heightPx ?? height_px,
    previewUrl: rest.previewUrl ?? preview_url,
  }
}

const normalizeCategory = category => ({
  publicId: category.public_id,
  name: category.name,
  count: category.count,
})

// One page of the shop's designs, optionally searched and narrowed to one category. Always
// sends `page`, which is what asks the server for the paged envelope instead of the bare
// array of every design. The first page's envelope also carries the shop's categories with
// counts; later pages omit them.
export async function fetchVendorArtworks(
  vendorToken,
  builderSessionId,
  builderDraftPublicId,
  { page = 1, q = '', category = '' } = {}
) {
  const query = new URLSearchParams({ page: String(page) })
  if (q.trim()) query.set('q', q.trim())
  if (category) query.set('category', category)

  const response = await fetch(`/builder/${vendorToken}/vendor_artworks?${query}`, {
    headers: buildHeaders(builderSessionId, builderDraftPublicId),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(data.error || 'Could not load shop designs')

  // A server from before paging answers with the bare array, e.g. mid-deploy.
  if (Array.isArray(data)) {
    return { artworks: data.map(normalizeVendorArtwork), page: 1, nextPage: null, total: data.length, categories: [] }
  }

  return {
    artworks: (data.artworks || []).map(normalizeVendorArtwork),
    page: data.page ?? page,
    nextPage: data.next_page ?? null,
    total: data.total ?? 0,
    categories: (data.categories || []).map(normalizeCategory),
  }
}

export async function importVendorArtwork(
  vendorToken,
  builderSessionId,
  publicId,
  builderDraftPublicId
) {
  const response = await fetch(
    `/builder/${vendorToken}/vendor_artworks/${encodeURIComponent(publicId)}/import`,
    {
      method: 'POST',
      headers: buildHeaders(builderSessionId, builderDraftPublicId),
      body: JSON.stringify({ builder_draft_public_id: builderDraftPublicId }),
    }
  )
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(data.error || 'Could not add the shop design')
  return normalizeUpload(data)
}

export async function fetchUploadStatuses(vendorToken, builderSessionId, ids, builderDraftPublicId) {
  const params = ids.map(id => `ids[]=${id}`).join('&')
  const response = await fetch(`/builder/${vendorToken}/uploads?${params}`, {
    headers: buildHeaders(builderSessionId, builderDraftPublicId)
  })
  if (!response.ok) throw new Error('Failed to fetch statuses')
  const uploads = await response.json()
  return uploads.map(normalizeUpload)
}

export async function createUpload(vendorToken, builderSessionId, blobSignedId, builderDraftPublicId, options = {}) {
  const headers = buildHeaders(builderSessionId, builderDraftPublicId)
  const multipart = blobSignedId instanceof Blob
  let body
  if (multipart) {
    body = new FormData()
    body.append('file', blobSignedId)
    if (builderDraftPublicId) body.append('builder_draft_public_id', builderDraftPublicId)
    delete headers['Content-Type'] // The browser supplies the multipart boundary.
  } else {
    body = JSON.stringify({ blob_signed_id: blobSignedId, builder_draft_public_id: builderDraftPublicId })
  }
  const request = async signal => {
    const response = await fetch(`/builder/${vendorToken}/uploads`, {
      method: 'POST', headers, body,
      ...(signal && { signal }),
    })
    let data
    try { data = await response.json() } catch {
      if (response.ok) throw new Error('Upload returned an invalid response. Please try again.')
      data = {}
    }
    if (!response.ok) throw new Error(data?.error || 'upload failed')
    if (!Number.isInteger(Number(data?.id)) || Number(data.id) <= 0) {
      throw new Error('Upload returned an invalid response. Please try again.')
    }
    return normalizeUpload(data)
  }
  return multipart ? withUploadDeadline(request, options.signal) : request(options.signal)
}

export async function deleteUpload(vendorToken, builderSessionId, id, builderDraftPublicId) {
  const response = await fetch(`/builder/${vendorToken}/uploads/${id}`, {
    method: 'DELETE',
    headers: buildHeaders(builderSessionId, builderDraftPublicId)
  })
  if (!response.ok) throw new Error('Failed to delete upload')
}

export async function fetchImageUrl(vendorToken, builderSessionId, id, builderDraftPublicId, signal, delivery) {
  if (delivery === 'app') {
    return fetchArtworkPreview(`/builder/${vendorToken}/uploads/${id}/image?preview_delivery=app`, {
      headers: buildHeaders(builderSessionId, builderDraftPublicId),
      ...(signal && { signal }),
    })
  }
  const response = await fetch(`/builder/${vendorToken}/uploads/${id}/image`, {
    headers: buildHeaders(builderSessionId, builderDraftPublicId),
    ...(signal && { signal }),
  })
  if (!response.ok) throw new Error('Failed to fetch image URL')
  const data = await response.json()
  return data.url
}

export async function fetchBackgroundPreview(
  vendorToken,
  builderSessionId,
  id,
  mode,
  builderDraftPublicId
) {
  const response = await fetch(
    `/builder/${vendorToken}/uploads/${id}/background_preview?mode=${encodeURIComponent(mode)}`,
    { headers: buildHeaders(builderSessionId, builderDraftPublicId) }
  )
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Could not generate a background preview')
  }
  return response.blob()
}

export async function updateUploadBackground(
  vendorToken,
  builderSessionId,
  id,
  mode,
  builderDraftPublicId
) {
  const response = await fetch(`/builder/${vendorToken}/uploads/${id}/background`, {
    method: 'PATCH',
    headers: buildHeaders(builderSessionId, builderDraftPublicId),
    body: JSON.stringify({ mode }),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(data.error || 'Could not update the artwork background')
  return normalizeUpload(data)
}

export async function createActiveDraft(vendorToken, builderSessionId, vendorSheetSizeId) {
  const response = await fetch(`/builder/${vendorToken}/drafts/active`, {
    method: 'POST',
    headers: buildHeaders(builderSessionId),
    body: JSON.stringify({ vendor_sheet_size_id: vendorSheetSizeId }),
  })
  const data = await response.json()
  if (!response.ok) throw new Error(data.error || 'Could not create draft')
  return data
}

export async function claimBuilderDraft(vendorToken, builderSessionId, publicId) {
  const response = await fetch(`/builder/${vendorToken}/drafts/${publicId}/claim`, {
    method: 'POST',
    headers: buildHeaders(builderSessionId, publicId),
    body: JSON.stringify({}),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(data.error || 'Could not save this gang sheet to your account')
  return data
}

export async function startNewBuilderDraft(
  vendorToken,
  builderSessionId,
  publicId,
  vendorSheetSizeId
) {
  const response = await fetch(`/builder/${vendorToken}/drafts/${publicId}/start_new`, {
    method: 'POST',
    headers: buildHeaders(builderSessionId, publicId),
    body: JSON.stringify({ vendor_sheet_size_id: vendorSheetSizeId }),
  })
  const data = await response.json()
  if (!response.ok) throw new Error(data.error || 'Could not start a new gang sheet')
  return data
}

export async function fetchBuilderDraft(vendorToken, builderSessionId, publicId) {
  const response = await fetch(`/builder/${vendorToken}/drafts/${publicId}`, {
    headers: buildHeaders(builderSessionId, publicId),
  })
  const data = await response.json()
  if (!response.ok) throw new Error(data.error || 'Could not load draft')
  return data
}

export async function updateBuilderDraft(vendorToken, builderSessionId, publicId, payload, { keepalive = false } = {}) {
  const response = await fetch(`/builder/${vendorToken}/drafts/${publicId}`, {
    method: 'PATCH',
    headers: buildHeaders(builderSessionId, publicId),
    body: JSON.stringify(payload),
    keepalive,
  })
  const data = await response.json()
  if (!response.ok) {
    const error = new Error(data.error || 'Could not save draft')
    error.status = response.status
    error.code = data.code
    error.data = data
    throw error
  }
  return data
}

export async function requestBuilderCustomerSignIn(vendorToken, email, builderDraftPublicId) {
  const response = await fetch(`/builder/${vendorToken}/customer/builder_magic_links`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      'X-Builder-CSRF': builderCsrfToken(),
    },
    body: JSON.stringify({ email, builder_draft_public_id: builderDraftPublicId }),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(data.error || 'Could not request a sign-in link')
  return data
}

export async function fetchCustomerSessionStatus(vendorToken) {
  const response = await fetch(`/builder/${vendorToken}/customer/session`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { 'Accept': 'application/json' },
  })
  if (!response.ok) throw new Error('Could not check customer session')
  return response.json()
}

export async function completeCustomerSignInHandoff(
  vendorToken,
  builderSessionId,
  builderDraftPublicId,
  handoffToken
) {
  const response = await fetch(`/builder/${vendorToken}/customer/session/handoff`, {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      ...buildHeaders(builderSessionId, builderDraftPublicId),
      'Accept': 'application/json',
    },
    body: JSON.stringify({ handoff_token: handoffToken }),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(data.error || 'Could not complete customer sign-in')
  return data
}
